/**
 * Shared coordinate transform utilities for the canvas editor.
 *
 * All functions are pure -- no side effects or DOM dependencies beyond
 * the canvas element's bounding rect.
 */
import type { Point } from './canvaskit-hit-test';
import type { SceneNode } from '@/lib/scene-document';

// ─── Viewport transforms ──────────────────────────────────────────────

/** Convert DOM clientX/clientY to canvas-local pixel coords. */
export function screenToCanvas(
  clientX: number,
  clientY: number,
  canvasEl: HTMLCanvasElement,
): Point {
  const rect = canvasEl.getBoundingClientRect();
  // Compensate for ancestor CSS transforms (e.g. EmailCanvasViewport's scale):
  // rect.{width,height} are post-transform, offsetWidth/Height are pre-transform.
  const scaleX = canvasEl.offsetWidth ? rect.width / canvasEl.offsetWidth : 1;
  const scaleY = canvasEl.offsetHeight ? rect.height / canvasEl.offsetHeight : 1;
  return {
    x: (clientX - rect.left) / (scaleX || 1),
    y: (clientY - rect.top) / (scaleY || 1),
  };
}

/** Canvas-local coords to scene (document) space. */
export function canvasToScene(
  canvasX: number,
  canvasY: number,
  zoom: number,
  panOffset: Point,
): Point {
  return {
    x: (canvasX - panOffset.x) / zoom,
    y: (canvasY - panOffset.y) / zoom,
  };
}

/** Scene (document) space to canvas-local coords. */
export function sceneToCanvas(
  sceneX: number,
  sceneY: number,
  zoom: number,
  panOffset: Point,
): Point {
  return {
    x: panOffset.x + sceneX * zoom,
    y: panOffset.y + sceneY * zoom,
  };
}

/** Convenience: DOM clientX/clientY straight to scene space. */
export function screenToScene(
  clientX: number,
  clientY: number,
  canvasEl: HTMLCanvasElement,
  zoom: number,
  panOffset: Point,
): Point {
  const c = screenToCanvas(clientX, clientY, canvasEl);
  return canvasToScene(c.x, c.y, zoom, panOffset);
}

// ─── Workspace ↔ frame transforms ─────────────────────────────────────

/**
 * Convert a point in workspace scene space to an inner frame's scene space.
 * Accounts for the frame's position in the workspace and the inner viewport.
 */
export function workspaceToFrameScene(
  wsScenePoint: Point,
  frameNode: Pick<SceneNode, 'x' | 'y'>,
  innerZoom: number,
  innerPan: Point,
): Point {
  // Translate so frame origin is (0,0), then apply inner viewport inverse
  const relX = wsScenePoint.x - frameNode.x;
  const relY = wsScenePoint.y - frameNode.y;
  return {
    x: (relX - innerPan.x) / innerZoom,
    y: (relY - innerPan.y) / innerZoom,
  };
}

/**
 * Convert a point in an inner frame's scene space to workspace scene space.
 * Reverse of workspaceToFrameScene.
 */
export function frameSceneToWorkspace(
  frameScenePoint: Point,
  frameNode: Pick<SceneNode, 'x' | 'y'>,
  innerZoom: number,
  innerPan: Point,
): Point {
  return {
    x: frameNode.x + innerPan.x + frameScenePoint.x * innerZoom,
    y: frameNode.y + innerPan.y + frameScenePoint.y * innerZoom,
  };
}

// ─── Chrome offset helper ──────────────────────────────────────────────

/**
 * Compute the canvas-pixel offset for rendering inner-frame chrome
 * (selection boxes, handles, snap guides, etc.) on the workspace surface.
 *
 * Returns { offsetX, offsetY, combinedZoom } ready to pass to draw* functions.
 */
export function innerChromeTransform(
  wsZoom: number,
  wsPanOffset: Point,
  frameNode: Pick<SceneNode, 'x' | 'y'>,
  innerZoom: number,
  innerPan: Point,
): { offsetX: number; offsetY: number; combinedZoom: number } {
  const baseX = wsPanOffset.x + frameNode.x * wsZoom;
  const baseY = wsPanOffset.y + frameNode.y * wsZoom;
  return {
    offsetX: baseX + innerPan.x * wsZoom,
    offsetY: baseY + innerPan.y * wsZoom,
    combinedZoom: wsZoom * innerZoom,
  };
}
